import gql from 'graphql-tag';
import React, {useEffect, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import SourceDAO from 'file-browser/SourceDAO';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {OptionData} from 'ui-library/menus/MenuConstants';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import {
  OrganizationReviewLifecyclesQuery,
  OrganizationReviewLifecyclesQueryVariables,
} from './__generated/OrganizationReviewLifecyclesQuery';
import {
  ProjectOrganizationsQuery,
  ProjectOrganizationsQueryVariables,
} from './__generated/ProjectOrganizationsQuery';
import ReviewProcessSelector, {generateReviewProcessSelectorOptions} from './ReviewProcessSelector';

type ProjectOrInaccessibleGraphQL = ProjectOrganizationsQuery['projects'][number];

type ProjectGraphQL = ExtractByTypename<ProjectOrInaccessibleGraphQL, 'ProjectNode'>;

const USER_OWNED_PROJECT_ERROR_MESSAGE = 'Review processes are not supported for user-owned projects.';
const DIFFERENT_ORGANIZATIONS_ERROR_MESSAGE = 'All selected projects must belong to the same organization.';

export const PROJECT_ORGANIZATIONS_QUERY = gql`
  query ProjectOrganizationsQuery($projectIds: [StrictString!]!) @skipAudit @engComponent(name: NOTEBOOK) {
    projects(ids: $projectIds) {
      ... on ProjectNode {
        id
        ownerJson
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const ORGANIZATION_REVIEW_LIFECYCLES_QUERY = gql`
  query OrganizationReviewLifecyclesQuery($organizationId: StrictString!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    organization(id: $organizationId) {
      ... on OrganizationNode {
        id
        reviewLifecycles {
          id
          currentVersion {
            id
            name
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface BulkUpdateProjectReviewLifecycleModalProps {
  projectIds: Array<string>;
  onRequestClose: () => void;
}

interface ReassignProjectsReviewLifecycleModalProps {
  organizationId: string;
  projectIds: Array<string>;
  onRequestClose: () => void;
}

function ReassignProjectsReviewLifecycleModal({
  organizationId,
  projectIds,
  onRequestClose,
}: ReassignProjectsReviewLifecycleModalProps): JSX.Element | null {
  const result = useBenchlingQuery<
    OrganizationReviewLifecyclesQuery,
    OrganizationReviewLifecyclesQueryVariables
  >(ORGANIZATION_REVIEW_LIFECYCLES_QUERY, {
    variables: {organizationId},
    fetchPolicy: 'network-only',
  });

  const [isAuditorBasedReview, setIsAuditorBasedReview] = useState(false);
  const [selectedReviewLifecycleIds, setSelectedReviewLifecycleIds] = useState<Array<string>>([]);

  function selectReviewLifecycleId(reviewLifecycleId: string): void {
    setSelectedReviewLifecycleIds((previouslySelected) => {
      if (!previouslySelected.includes(reviewLifecycleId)) {
        return previouslySelected.concat([reviewLifecycleId]);
      }
      return previouslySelected.filter((rlcId) => rlcId !== reviewLifecycleId);
    });
  }

  function removeReviewLifecycleId(reviewLifecycleId: string): void {
    setSelectedReviewLifecycleIds(selectedReviewLifecycleIds.filter((id) => id !== reviewLifecycleId));
  }

  if (result.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    return null;
  }

  const onRequestSubmit = async (): Promise<void> => {
    window.onbeforeunload = () => '';
    await withLoadingScreen(
      SourceDAO.bulkSetProjectsReviewLifecycle({
        projectApiIds: projectIds,
        reviewLifecycleApiIds: selectedReviewLifecycleIds,
      })
    );
    notifications.success('Successfully updated review process on selected projects.');
    window.onbeforeunload = null;
    onRequestClose();
  };

  const {organization} = result.data;
  if (isInaccessible(organization)) {
    assertAndContinue(false, 'User did not have access to the organization');
    return null;
  }

  const reviewProcessOptions: Array<OptionData> = generateReviewProcessSelectorOptions({
    reviewLifecycleIdAndNames: organization.reviewLifecycles.map((reviewLifecycle) => ({
      id: reviewLifecycle.id,
      name: reviewLifecycle.currentVersion.name,
    })),
    selectedReviewLifecycleIds,
    isMulti: true,
  });

  return (
    <Modal
      closeable={true}
      title="Change review processes for the selected projects"
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
          style: ButtonStyle.TERTIARY_GRAY,
        },
        {
          text: 'Update',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: selectedReviewLifecycleIds.length === 0 && !isAuditorBasedReview,
        },
      ]}
    >
      <div style={{marginBottom: '8px'}}>
        The new selected options will replace current project configuration for the selected projects
        entirely.
      </div>
      <ReviewProcessSelector
        includeAuditorBasedReviewOption={true}
        isAuditorBasedReview={isAuditorBasedReview}
        setIsAuditorBasedReview={setIsAuditorBasedReview}
        isMulti={true}
        showWarnings={false}
        selectReviewLifecycleId={(reviewLifecyleId) => {
          selectReviewLifecycleId(reviewLifecyleId);
        }}
        removeReviewLifecycleId={removeReviewLifecycleId}
        options={reviewProcessOptions}
        selectedReviewLifecycleIds={selectedReviewLifecycleIds}
      />
    </Modal>
  );
}

interface ProjectValidationResult {
  errorMessage: string | null;
  projects: Array<ProjectGraphQL> | null;
}

function validateProjects(projects: Array<ProjectOrInaccessibleGraphQL>): ProjectValidationResult {
  const accessibleProjects: Array<ProjectGraphQL> = [];
  for (const project of projects) {
    if (isInaccessible(project)) {
      assertAndContinue(false, 'User did not have access to a project');
      return {errorMessage: null, projects: null};
    }
    if (project.ownerJson.type !== 'organization') {
      return {errorMessage: USER_OWNED_PROJECT_ERROR_MESSAGE, projects: null};
    }
    accessibleProjects.push(project);
  }
  return {errorMessage: null, projects: accessibleProjects};
}

export default function BulkUpdateProjectReviewLifecycleModal({
  projectIds,
  onRequestClose,
}: BulkUpdateProjectReviewLifecycleModalProps): JSX.Element | null {
  const result = useBenchlingQuery<ProjectOrganizationsQuery, ProjectOrganizationsQueryVariables>(
    PROJECT_ORGANIZATIONS_QUERY,
    {
      variables: {projectIds},
      fetchPolicy: 'network-only',
    }
  );

  let organizationId: string | null = null;
  let projectValidationErrorMessage: string | null = null;
  let projectsAreValid = false;
  if (result.type === QueryResultType.SUCCESS) {
    const projectValidationResult = validateProjects(result.data.projects);
    if (projectValidationResult.errorMessage != null) {
      projectValidationErrorMessage = projectValidationResult.errorMessage;
    } else if (projectValidationResult.projects != null) {
      const organizationIds = new Set(
        projectValidationResult.projects.map((project) => project.ownerJson.id)
      );
      if (organizationIds.size === 1) {
        organizationId = [...organizationIds][0];
        projectsAreValid = true;
      } else {
        projectValidationErrorMessage = DIFFERENT_ORGANIZATIONS_ERROR_MESSAGE;
      }
    }
  }

  useEffect(() => {
    if (projectValidationErrorMessage != null) {
      notifications.error(projectValidationErrorMessage);
    }
  }, [projectValidationErrorMessage]);

  if (result.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    return null;
  }

  if (!projectsAreValid || organizationId == null) {
    return null;
  }

  return (
    <ReassignProjectsReviewLifecycleModal
      organizationId={organizationId}
      projectIds={projectIds}
      onRequestClose={onRequestClose}
    />
  );
}
